import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { CustomerType, IBrand, PPW_BRAND, RSW_BRAND } from '../../models/brand.model';

const PPW_HOST_MARKER = 'propainterwebsites';
/** Also read by the pre-paint script in index.html — keep the key in sync. */
const BRAND_STORAGE_KEY = 'portal-brand';

@Injectable({ providedIn: 'root' })
export class BrandService {
  private title = inject(Title);

  brand: IBrand = BrandService.resolveInitialBrand();

  setCustomerType(customerType: CustomerType | null | undefined): void {
    const resolved = customerType === CustomerType.PPW ? PPW_BRAND : RSW_BRAND;
    this.storeBrandKey(resolved.key);

    if (resolved.key !== this.brand.key) {
      this.brand = resolved;
      this.applyTheme();
    }
  }

  /** Called on logout so the next sign-in on this host does not inherit the previous brand. */
  clearStoredBrand(): void {
    try {
      localStorage.removeItem(BRAND_STORAGE_KEY);
    } catch {
      /* localStorage unavailable — nothing to clear */
    }
  }

  applyTheme(): void {
    document.documentElement.setAttribute('data-brand', this.brand.key);
    this.title.setTitle(this.brand.title);

    let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      document.head.appendChild(icon);
    }
    icon.href = this.brand.favicon;
  }

  /**
   * Mirrors the pre-paint script in index.html: the PPW hostname always wins, otherwise
   * fall back to the brand this browser last resolved from the customer's type.
   */
  private static resolveInitialBrand(): IBrand {
    if (window.location.hostname.includes(PPW_HOST_MARKER)) {
      return PPW_BRAND;
    }

    try {
      return localStorage.getItem(BRAND_STORAGE_KEY) === PPW_BRAND.key ? PPW_BRAND : RSW_BRAND;
    } catch {
      return RSW_BRAND;
    }
  }

  private storeBrandKey(key: IBrand['key']): void {
    try {
      localStorage.setItem(BRAND_STORAGE_KEY, key);
    } catch {
      /* localStorage unavailable — brand still applies for this page load */
    }
  }
}
